import { useTheme } from "@emotion/react";
import { Attachment as AttachmentIcon, Delete as DeleteIcon, Print as PrintIcon } from '@mui/icons-material';
import CodeIcon from '@mui/icons-material/Code';
import SaveIcon from '@mui/icons-material/Save';
import SignLanguageIcon from '@mui/icons-material/SignLanguage';
import { Button, Grid } from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Swal from 'sweetalert2';
import strings from "../Globalization/index";
import { dataStore } from '../Store';
import ActionIconButton from "../components/common/ActionIconButton";
import LoadingDialog from "../components/common/LoadingDialog";
import consts from "../consts";
import { useBreadcrumbs } from "../contexts/Breadcrumbs";
import { useSetState, useUniqueEffect } from "../hooks";
import connectionService from "../services/Connection";
import util from '../util/index';
import XmlEditorDialog from "../contexts/CycleInfo/XmlEditorDialog";
import { onError } from "../components/crud-helper";
import IncubatorForm from './forms/IncubatorCycleDetailForm';

const { NONSTERISHP, NONSTERISSTEAM } = consts.EquipmentFamilyType;
const DefaultWellNumber = 20;


const CycleAttachmentsBreadcrumbAction = (param) => {
	const [searchParams] = useSearchParams()
	const cycleId = searchParams.get('cycleId') || 0;
	return (
		<ActionIconButton component={Link} variant="contained" size="small" className="no-print"
			to={`/cycleattachmentlist?AssociationId=${cycleId}&AssociationType=${consts.Attachment.AttachmentType}&AssociationTypeId=${consts.Attachment.AssociationType}`}
			title={strings.CycleAttachments} color="primary">
			<AttachmentIcon />
		</ActionIconButton>
	)
};

const CyclePrintBreadcrumbAction = () => {
	const printPage = () => {
		let gridCss = ``;
		for (let i = 1; i <= 12; i++) {
			gridCss += `.MuiGrid-grid-md-${i}.inputWrapper, .MuiGrid-grid-md-${i}.inlineLabelContainer, .MuiGrid-grid-md-${i}.inlineInputContainer, .MuiGrid-grid-lg-${i}.inputWrapper, .MuiGrid-grid-lg-${i}.inlineLabelContainer, .MuiGrid-grid-lg-${i}.inlineInputContainer { 
				flex-basis: auto !important; 
				max-width: calc(100% * ${i} / var(--Grid-parent-columns) - (var(--Grid-parent-columns) - ${i}) * (var(--Grid-parent-columnSpacing) / var(--Grid-parent-columns))) !important; 
			}
			`;
		}
		const css = `
				@page {
					size: 210mm 297mm;
					margin: 0;
				}
				@media print { 
					@page {
						size: 210mm 297mm;
					}
					#main-page-wrapper {
						width: 210mm;
						height: 297mm;
					}
					.breadcrumb-card-content {
						padding: 4px 8px !important;
					}
					.breadcrumb-card-content .MuiBreadcrumbs-ol .MuiTypography-root {
						font-size: 22px;						
					}
					.MuiCard-root, .MuiIconButton-root, .MuiButton-contained {
						box-shadow: none !important;
					}
					form .MuiCardContent-root {
						padding: 4px 8px !important;
					}
					.inputWrapper {
						padding: 4px 0px;
						padding-left: 6px;
					}				
					.section-formgroup-header{
						padding: 4px;
						font-weight: bold;
					}
					${gridCss}
					.inputWrapper, .MuiIconButton-root, .MuiButton-contained {
						page-break-after: avoid;
					}
				}`;
		const head = document.head || document.getElementsByTagName('head')[0];
		const style = document.createElement('style');

		head.appendChild(style);
		style.type = 'text/css';
		if (style.styleSheet) {
			// This is required for IE8 and below.
			style.styleSheet.cssText = css;
		} else {
			style.appendChild(document.createTextNode(css));
		}
		window.onafterprint = () => {
			try {
				head.removeChild(style);
			} catch (error) {
				console.error('print remove child error', error);
			}
		};
		window.print();
		return true;
	};
	return (
		<ActionIconButton
			onClick={printPage}
			title={strings.Print}
			color="primary"
			variant="contained"
			size="small"
			className="no-print"
		>
			<PrintIcon />
		</ActionIconButton>
	);
};

const CycleDeleteBreadcrumbAction = () => {

	return (
		<Button title={strings.Delete} variant="contained" color="error" className="no-print"
			disabled startIcon={<DeleteIcon />} size="small" >
			{strings.Delete}
		</Button>
	);
};

const ViewXML = ({ xmlFileName, equipmentType, serialNumber }) => {
	const [state, setState] = useState({
		xmlEditorOpen: false,
		xmlEditorValue: '',
		loading: false,
		xmlEditorTitle:''
	});
	const [searchParams] = useSearchParams()
	const cycleId = searchParams.get('cycleId') || 0;

	const handleOnOpenXmlModal = () => {
		setState({ loading: true });
		const xmlEditorTitle = `${strings.XMLName} ${equipmentType}/${serialNumber}`;
		const formData = new FormData();
		formData.append('action', 'otherAction');
		formData.append('otherAction', 'PreviewXML');
		formData.append('cycleId', cycleId);
		fetch(connectionService.CycleList, {
			method: 'POST',
			body: formData
		})
			.then((res) => res.text())
			.then((text) => {
				setState({ xmlEditorOpen: true, xmlEditorValue: text, loading: false, xmlEditorTitle: xmlEditorTitle });
			})
			.catch((error) => setState({ loading: false, error }));
	};
	const handleOnCloseXmlModal = () => {
		setState({ xmlEditorOpen: false });
	};
	return (
		<>
			<ActionIconButton
				variant="contained"
				size="small"
				color="tertiary"
				className="no-print"
				onClick={handleOnOpenXmlModal}
			>
				<CodeIcon />
			</ActionIconButton>
			<XmlEditorDialog
				{...state}
				xmlFileName={xmlFileName}
				onCloseXmlEditorDialog={handleOnCloseXmlModal}
			/>
		</>
	)
}

const IncubatorCycleDetailForm = ({ 
	dataProperty = 'IncubatorCycleDetail',
	comboTypes = ['Indicator', 'BILotNumber', 'User', 'CILotNumber', 'ReusableTestPack', 'Sterilizer', 'Incubator', 'IncubatorUser']
}) => {
	const [, setBreadcrumbs] = useBreadcrumbs();
	const location = useLocation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const theme = useTheme();
	const GTIN = searchParams.get('gTIN') || null;
	const id = searchParams.get('id') || 0;
	const Key = searchParams.get('key') || 0;
	const attachmentCount = parseInt(searchParams.get('attachmentCount')) || 0;
	const cycleId = searchParams.get('cycleId') || 0;
	const incubatorCycleId = searchParams.get('incubatorCycleId');
	const incubatorId = searchParams.get('incubatorId');
	const isError = JSON.parse(searchParams.get('isError'));
	const isIncubator = searchParams.get('isIncubator') || true;
	const isFormCycleScreen = searchParams.get('isFromCycle');
	const prevRoutId = searchParams.get('prevId');
	const isFormScanBarcode = searchParams.get('formScanBarcode');
	const EquipmentId = searchParams.get('EquipmentId');

	const { roles } = useSelector(state => state?.auth);
	const [state, setState] = useSetState({
		isFormValidate: false,
		preIncubation: true,
		isSterilized: false,
		isIncubated: false,
		canSelectSterilizer: false,
		isStream: false,
		openPopUp: false,
		saveDisable: true,
		showAcknowledgeBtn: false,
		isControl: false,
		maxWellNumber: DefaultWellNumber,
		temperatureUnit: '',
		isAcknowledge: false,
		isConectivityTypeIsNone: false,
		equipmentId: 0,
		key: 0,
		msgBINotLinkedShowed: false,
		isError: false,
		sterilizerCycleId: consts.isNodeServer ? '' : 0,
		connectivityType: 0,
		title: 'Load Detail',
		oldGTIN: 'new',
		sterilizerCycleNumber: 0,
		activeRecord: null,
		loading: false,
		json: {},
		addAttachmnet: attachmentCount !== 0 ? true : false,
		canDelete: false,
		isCycleCountFound: false,
		prevPath: location.pathName,
		isFormScanBarcode: false,
		isReadByDisable: false,
		isIncubatorIdDisable: false
	}
	);
	let config = { LoadNumber: '', SterilizerMachineLength: 0, SterilizerCycleNumberLength: 0, DateFormat: '', SplitChar: '' };
	const breadCrumbActions = useMemo(() => {

		const isNew = state.activeRecord?.record?.isNew ?? true;
		const actionsArr = [
			{ component: CycleDeleteBreadcrumbAction },
			{ component: CycleAttachmentsBreadcrumbAction },
			{ component: CyclePrintBreadcrumbAction }
		];
		const { record = {} } = state.activeRecord || {};
		const viewXmlProps = {
			xmlFileName: record.StorageFilename,
			equipmentType: record.EquipmentType,
			serialNumber: record.SerialNumber
		}
		if (roles?.includes?.('SuperAdmin') && !isNew) {
			actionsArr.push({ component: ViewXML, ...viewXmlProps });
		}
		return actionsArr;
	}, [roles, state.activeRecord]);

	useUniqueEffect(() => {
		setBreadcrumbs({
			breadcrumbs: [{ text: `Load Detail` }, { text: `Incubator` }],
			actions: breadCrumbActions,
			docTitle: `Load Detail | ${strings.Incubator}`
		});
	}, [breadCrumbActions]);


	useUniqueEffect(() => {

		fetchData();
	}, [GTIN, isError])


	const onLoadBICILotNumber = (id, arr) => {
		let expiredDate = dayjs().subtract(util.checkConfigData('BIExpiredDaysAccess'), 'day');

		return Array.isArray(arr) ? arr.map(function (item, index) {
			if (dayjs(util.customDateTimeDisplay(item.ExpiryDate, consts.MomentFormat.Date)).isBefore(dayjs()) || dayjs(item.ExpiryDate).isBefore(dayjs())) {
				item.className = "expired-lot";
			}
			return item;
		}).filter(function (bilot) {
			return bilot.LookupId === id || dayjs(util.customDateTimeDisplay(bilot.ExpiryDate, consts.MomentFormat.Date)).isAfter(expiredDate) || dayjs(bilot.ExpiryDate).isAfter(expiredDate);
		}) : [];
	};

	const onDataLoaded = async (data, combos) => {
		if (data && Object.keys(data).length !== 0) {
			const sterilizerComboData = combos.Sterilizer.find(item => item.LookupId === data.SterilizerId);
			const isNonSteris = [NONSTERISHP, NONSTERISSTEAM].includes(sterilizerComboData && sterilizerComboData.EquipmentFamilyId);

			combos.BILotNumber = onLoadBICILotNumber(data.BILotNumberId, combos.BILotNumber);
			combos.CILotNumber = onLoadBICILotNumber(data.CILotNumber, combos.CILotNumber);

			let gTIN = GTIN,
				{ key } = Key;
			Object.assign(state, { oldGTIN: gTIN, oldKey: key });
			let loginData = util.localData.loginData || {};
			data.Well = data.Well ? data.Well : 0;
			if (loginData.tags) {
				let loadNumExist = false;
				if (data.LoadNumber) {
					loadNumExist = true;
				}
				let loadNumberFormat = util.localData.LoadNumberFormat ? util.localData.LoadNumberFormat.split('/') : loginData.tags.LoadNumberFormat.split('/');
				loadNumberFormat.forEach(value => {
					switch (value) {
						case 'M2':
						case 'M3':
							config.SterilizerMachineLength = value === 'M2' ? 2 : 3;
							break;
						case 'C2':
						case 'C3':
							config.SterilizerCycleNumberLength = value === 'C2' ? 2 : 3;
							break;
						default:
							if (!loadNumExist) {
								if (value !== '_' && value !== '-') {
									config.DateFormat += value;
								}
								else {
									config.LoadNumber += value;
								}
								data.LoadNumber += dayjs().format(value);
							}
							else {
								config.LoadNumber += value;
							}
					}
				});
				config.LoadNumber = data.LoadNumber;
				config.DefaultLoadNumber = loadNumExist ? "" : data.LoadNumber;
				config.loadNumExist = loadNumExist;
				config.validUserInputLength = config.SterilizerMachineLength + config.SterilizerCycleNumberLength + data.LoadNumber.length;
			}
			if (gTIN && !data.AcknowledgeTime) {
				data = processGTINDetail(gTIN, combos, data);
			}
			data.IncubatorId = data.IncubatorId ? data.IncubatorId : incubatorId || '';
			state.enableCycleCountandCycleName = isNonSteris;
			if (data.Id !== 0) {
				state.preIncubation = !(!!data.IsSterilized || !!data.IsIncubated)
				state.isSterilized = !!data.IsSterilized;
				state.isIncubated = !!data.IsIncubated;
				state.temperatureUnit = data.TemperatureUnit;
				state.isAcknowledge = !!data.AcknowledgeTime;
				state.saveDisable = !!state.isAcknowledge;

				if (data.TestOrControl === consts.TestOrControl.Test) {
					state.showAcknowledgeBtn = isNonSteris && !state.isAcknowledge ? true : !state.isAcknowledge && (!!data.IsIncubated || !!data.IsSterilized);
				}
				else {
					state.showAcknowledgeBtn = isNonSteris && !state.isAcknowledge ? true : !state.isAcknowledge && !!data.IsIncubated;
				}
				if (data.ConnectivityType === consts.ConnectivityType.None) {
					state.isConectivityTypeIsNone = true;
					state.isSterilized = true;
				}
				state.sterilizerCycleId = data.SterilizerCycleId;
				state.connectivityType = data.ConnectivityType;
			}
			else {
				let isIncubated = data.IncubatorCycleId > 0;
				state.isSterilized = data.SterilizerId > 0;
				state.isIncubated = !!isIncubated;
				state.preIncubation = !isIncubated;
				state.showAcknowledgeBtn = isNonSteris && !state.isAcknowledge ? true : !state.showAcknowledgeBtn && isIncubated && state.isSterilized;
			}
			if (state.isError) {
				state.saveDisable = true;
				state.showAcknowledgeBtn = isNonSteris && !state.isAcknowledge ? true : !data.AcknowledgeTime;
				state.canDelete = false;
			}
			else {
				switch (data["TestOrControl"]) {
					case consts.TestOrControl.Test:
						state.isControl = false;
						state.canDelete = !(data.SterilizerCycleId || data.IncubatorCycleId || data.BIResult);
						break;
					case consts.TestOrControl.Control:
						state.isControl = true;
						state.canDelete = !(data.IncubatorCycleId || data.BIResult);
						data.LoadNumber = '';
						break;
						default:
						break;
				}
			}
			state.json.data = Object.assign({}, state.json.data)
			let incubatorCombo = combos.Incubator || null;
			let comboData = util.getDataFromValue(incubatorCombo, data.IncubatorId, 'LookupId');
			state.maxWellNumber = comboData && comboData.WellCount !== 0 && comboData.WellCount ? comboData.WellCount : DefaultWellNumber;
			state.isNon = state.enableCycleCountandCycleName ? false : state.isIncubated;
			state.isReadByDisable = state.enableCycleCountandCycleName ? true : state.isAcknowledge;
			state.isIncubatorIdDisable = Number(id) === 0 ? false : (state.isAcknowledge || state.isIncubated || (!state.isSterilized && !state.isControl))
			state.isWellDisable = Number(id) === 0 ? false : state.isIncubated ? true : state.isAcknowledge ? ((!state.isSterilized && !state.isControl)) : false;
			if (data.TestOrControl === consts.incubatorControl) {
				data.LoadNumber = '';
			}
			if (gTIN && !data.AcknowledgeTime) {
				data.Test = true;
			}
			if (!data.id && !data.IncubatorId) {
				data.IncubatorId = combos?.Incubator?.find((inc) => inc?.LookupId === EquipmentId)?.LookupId;
			}
			data.isError = GTIN ? false : isError;

		}
		return { combos, record: data };
	}

	const fetchData = useCallback(async () => {


		setState({ loading: true });
		const formData = new FormData();
		formData.append('authToken', null);
		formData.append('id', id);
		formData.append('attachmentCount', attachmentCount);
		formData.append('cycleId', cycleId);
		formData.append('incubatorCycleId', incubatorCycleId);
		formData.append('isError', isError);
		formData.append('incubatorId', incubatorId);
		formData.append('GTIN', GTIN);
		formData.append('action', 'load');

		formData.append(
			'comboTypes',
			JSON.stringify([
				{ "type": "Indicator", "loaded": true },
				{ "type": "BILotNumber", "loaded": true },
				{ "type": "User", "loaded": true },
				{ "type": "CILotNumber", "loaded": true },
				{ "type": "ReusableTestPack", "loaded": true },
				{ "type": "Sterilizer", "loaded": true },
				{ "type": "Incubator", "loaded": true },
				{ "type": "IncubatorUser", "loaded": true }
			])
		);
		fetch(connectionService.IncubatorCycleDetail, { method: 'POST', body: formData })
			.then(res => res.json())
			.then(async ({ success, data, combos }) => {
				if (success) {
					const updatedActiveRecord = await onDataLoaded(data, combos);
					const isNew = updatedActiveRecord.record.id === 0;
					const isAcknowledged = !!updatedActiveRecord.record.AcknowledgeTime;
					if (isAcknowledged) {
						combos.User = combos.IncubatorUser
					}
					setState({
						activeRecord: {
							...updatedActiveRecord,
							record: {
								...updatedActiveRecord.record,
								isSterilized: state.isSterilized,
								isIncubated: state.isIncubated,
								isNew,
								isAcknowledge: !!updatedActiveRecord.record.AcknowledgeTime,
								isFormCycleScreen,
								isNon: state.isNon,
								prevRoutId: prevRoutId,
								isIncubator,
								EquipmentType: 'Incubator',
								EquipmentTypeId: consts.EquipmentType.INCUBATOR,
								isScanBarcode: isFormScanBarcode,
								isReadByDisable: state.isReadByDisable,
								isIncubatorIdDisable: state.isIncubatorIdDisable,
								Control: Boolean(updatedActiveRecord.record.TestOrControl),
								Test: !updatedActiveRecord.record.TestOrControl,
								isWellDisable: state.isWellDisable,
								TestOrControl: (data.TestOrControl === consts.incubatorControl && location?.state?.isControl === false) ? consts.incubatorControl  : location?.state?.isControl ? consts.incubatorControl  : consts.incubatorTest
							}
						}
					});
					location.state = { ...location.state, isRescan: location?.state?.isRescan, currentPageId: updatedActiveRecord.record.id, isControl: false };
				}
				else {
					Swal.fire({
						titleText: strings.Alert,
						text: strings.FailedToLoadData,
						closeOnConfirm: true,
						icon: "error",
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					})
				}
			}
			).catch(err => {
				Swal.fire({
					titleText: strings.Alert,
					text: strings.FailedToLoadData,
					closeOnConfirm: true,
					icon: "error",
					confirmButtonText: strings.Ok,
					allowOutsideClick: false
				})
			}).finally(() => setState({ loading: false }));

	}, [id, attachmentCount, cycleId, incubatorCycleId, isError, incubatorId, GTIN]);


	const handleOnSubmit = useCallback((values, { setFieldValue }) => {
		setState({ loading: true });
		const formData = new FormData();
		const comboTypesArr = Array.isArray(comboTypes)
			? comboTypes.map((comboType) => ({ type: comboType, loaded: true }))
			: [];
		for (const [key, value] of Object.entries(values)) {
			formData.append(key, value);
		}
		formData.append('action', 'save');
		formData.append('comboTypes', JSON.stringify(comboTypesArr));
		fetch(connectionService.IncubatorCycleDetail, {
			method: 'POST',
			body: formData
		})
			.then((res) => res.json())
			.then(({ success, data, info, err, infoMessage }) => {
				if (success) {
					setState({ loading: false });
					Swal.fire({ titleText: strings.Success, text: strings.ConfigurationSaveSuccessText, icon: 'success', confirmButtonText: strings.Ok, allowOutsideClick: false });
					handleAfterSave(data);
				} else {
					onError({ info, err, infoMessage });
					setFieldValue('LoadNumber', '');
				}

			}).catch(() => {
				setState({ loading: false });
				Swal.fire({ titleText: strings.ErrorTitle, text: strings.ConfigurationSaveErrorText, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
			})
			.finally(() => setState({ loading: false }));
	}, [state.activeRecord?.record]);


	const handleAcknowledgeClick = ({ formik, fieldName = 'AcknowledgeTime', activerecord }) => {
		const { auth = {} } = dataStore.getState() || {}
		const isCIRequiredWithBI = auth?.tags?.CIRequiredWithBI || false;
		const updatedJson = formik.values;
		const { Sterilizer } = activerecord?.combos || {};
		const sterilizerComboData = Sterilizer.find(item => item.LookupId === updatedJson?.SterilizerId);
		const isNonSteris = [NONSTERISHP, NONSTERISSTEAM].includes(sterilizerComboData && sterilizerComboData.EquipmentFamilyId);
		const { Control, IsIncubated, IsAcknowledge, RunById, BIReadById, SterilizerId, BILotNumberId, LoadNumber, CIReadById } = updatedJson;
		const isChemicalRequired = Control ? true : IsAcknowledge ? false : true;
		let isRequiredFieldEmpty = '';

		if (!RunById && !updatedJson.isError && updatedJson.isWellDisable) {
			isRequiredFieldEmpty = isRequiredFieldEmpty + (`</br><small style="color:${theme.palette.error.main};">${strings.IncubatedBy} is required </small>`);
		}
		if (!BILotNumberId && !updatedJson.isError && updatedJson.isWellDisable) {
			isRequiredFieldEmpty = isRequiredFieldEmpty + (`</br><small style="color:${theme.palette.error.main};">${strings.LotNumber} is required</small>`);
		}
		if (!BIReadById && !isNonSteris && !updatedJson.isError && updatedJson.isWellDisable) {
			isRequiredFieldEmpty = isRequiredFieldEmpty + (`</br><small style="color:${theme.palette.error.main};">${strings.ReadById} is required</small>`);
		}
		if (!SterilizerId && !Control && !updatedJson.isError && updatedJson.isWellDisable) {
			isRequiredFieldEmpty = isRequiredFieldEmpty + (`</br><small style="color:${theme.palette.error.main};">${strings.Sterilizer} is required</small>`);
		}
		if (!LoadNumber && !Control && !updatedJson.isError && updatedJson.isWellDisable) {
			isRequiredFieldEmpty = isRequiredFieldEmpty + (`</br><small style="color:${theme.palette.error.main};">${strings.LoadNumber} is required</small>`);
		}
		if (updatedJson.LoadNumber.split('-')[1] === "" && !updatedJson.isError && updatedJson.isWellDisable) {
			Swal.fire({
				title: strings.Info,
				text: strings.MachineValidation,
				closeOnConfirm: true,
				type: "info",
				confirmButtonText: strings.Ok,
				allowOutsideClick: false
			});
			return;
		}
		if (!updatedJson.Well && !IsIncubated && !Control) {
			Swal.fire({
				titleText: strings.ErrorTitle,
				text: strings.EnterWellInfo,
				icon: 'error',
				confirmButtonText: strings.Ok,
				confirmButtonColor: '#3085d6',
				allowOutsideClick: false
			});
			return;
		}
		if (isChemicalRequired && !Control && !updatedJson.isError && isCIRequiredWithBI) {
			if (!updatedJson.CILotNumberId || !updatedJson.CIReadById || !updatedJson.CIResult) {
				Swal.fire({
					titleText: strings.ErrorTitle,
					text: strings.EnterChemistryData,
					icon: 'error',
					confirmButtonText: strings.Ok,
					confirmButtonColor: '#3085d6',
					allowOutsideClick: false
				});
				return;
			}
		}
		if (isRequiredFieldEmpty) {
			Swal.fire({
				titleText: strings.ErrorTitle,
				html: isRequiredFieldEmpty,
				icon: 'error',
				confirmButtonText: strings.Ok,
				confirmButtonColor: '#3085d6',
				allowOutsideClick: false
			});
			return;
		}
		updatedJson.TestOrControl = updatedJson.Test ? consts.incubatorTest : consts.incubatorControl;
		updatedJson[fieldName] = dayjs().format(consts.MomentFormat.DateTimeFormat);
		setState({ json: updatedJson, isFormValidate: false });
		handleOnSubmit(updatedJson, {...formik});
	}



	const goBack = (isFormCycleScreen, activeRecord) => {
		const record = activeRecord ? activeRecord : location.state;
		if (JSON.parse(isFormCycleScreen)) {
			navigate(`/cyclelist`);
		} else {
			navigate(`/equipmentinfo?id=${record?.EquipmentId || record?.state?._id || record?.IncubatorId}&isSterilizer=${false}&isIncubator=${true}&isFromEquipmentDetail=${true}&equipmentType=${record?.EquipmentType || record?.state?.EquipmentType || 0}&equipmentTypeId=${record?.EquipmentTypeId || record?.state?.EquipmentTypeId || 0}`, { state: location.state });
		}
	};

	const handleAfterSave = (data) => {
		goBack(false, data);
	}

	const processGTINDetail = (barcodeScanValue, combos, record) => {
		const updatedJson = { record, combos };
		let barcodeScannedValueFound = false;
		let scanedValue = {};
		try {
			scanedValue = util.parseBarcode(barcodeScanValue);
			barcodeScannedValueFound = true;
		} catch (e) {
			barcodeScannedValueFound = false;
		}
		const aiValues = scanedValue?.parsedCodeItems;
		if (barcodeScannedValueFound && Array.isArray(aiValues)) {

			const lotOrBatchNo = aiValues.filter(function (e) { return e.ai === consts.BarcodeApplicationIdentifier.LotOrBatchNo });
			const barcodeSerialNumber = aiValues.filter(function (e) { return e.ai === consts.BarcodeApplicationIdentifier.SerialNo || e.ai === consts.BarcodeApplicationIdentifier.BarcodeSerialNumber });
			const bILotNumberCombo = updatedJson.combos ? updatedJson.combos.BILotNumber : null;
			const cILotNumberCombo = updatedJson.combos ? updatedJson.combos.CILotNumber : null;
			const bILotNumberData = util.getDataFromValue(bILotNumberCombo, lotOrBatchNo[0].data);
			const cILotNumberData = util.getDataFromValue(cILotNumberCombo, lotOrBatchNo[0].data);
			const lotNumberData = bILotNumberData || cILotNumberData || {};
			if (Object.keys(lotNumberData).length) {
				const cutomizedDateTime = util.customDateTimeDisplay(lotNumberData.ExpiryDate, consts.MomentFormat.Date);
				const expiryDate = dayjs(cutomizedDateTime).utcOffset(0, true);
				const today = dayjs();
				const graceExpiryDate = today.subtract(util.checkConfigData('BIExpiredDaysAccess'), 'day');
				const isExpiryInGracePeriod = expiryDate.isBetween(graceExpiryDate, today, 'day', '[]'); // '[]' means inclusive comparison
				if (today.isBefore(expiryDate) || isExpiryInGracePeriod) {
					if(cILotNumberData) {
						// Handle Chemical Indicator
						updatedJson.record = {
							...updatedJson.record,
							CILotNumberId: cILotNumberData.LookupId,
						}
					} else if(bILotNumberData) {
						// Handle Biological Indicator
						updatedJson.record = {
							...updatedJson.record,
							BILotNumberId: bILotNumberData.LookupId,
							IndicatorSerialNumber: barcodeSerialNumber.length ? barcodeSerialNumber[0].data : lotNumberData.SerialNumber,
							ExpiryDate: dayjs(expiryDate).format(consts.MomentFormat.Date),
							Implant: lotNumberData.IsSteam,
						}
	
					}
				}

			}
		}

		return updatedJson.record;
	}
	const CancelButton = ({ activeRecord, closeform }) => (<Button variant='contained' color='warning' startIcon={<GridCloseIcon />} onClick={closeform}>{strings.Cancel}</Button>)

	const AcknowledgeButton = ({ formik, activerecord, fieldName }) => (<Button disabled={!!state?.activeRecord?.record?.AcknowledgeTime} variant='contained' startIcon={<SignLanguageIcon />} onClick={() => (handleAcknowledgeClick({ formik, fieldName, activerecord }))}>{strings.Acknowledge}</Button>)
	const SaveButton = () => (<Button color="success" type="submit" disabled={!!state?.activeRecord?.record?.AcknowledgeTime || !!state?.activeRecord?.record?.isError} variant='contained' startIcon={<SaveIcon />}>{strings.Save}</Button>)

	return (
		<Grid container>
			<Grid size={12}>
				<IncubatorForm
					activeRecord={state.activeRecord}
					onSubmit={handleOnSubmit}
					hideCancel
					hideSubmit
					formActions={[
						{ Component: SaveButton },
						{ Component: CancelButton },
						{ Component: AcknowledgeButton },

					]}
				/>
			</Grid>
			<LoadingDialog open={state.loading} />
		</Grid>
	);
};
export default IncubatorCycleDetailForm;